import { Box, Center, Combobox, Group, useCombobox } from '@mantine/core';
import { UserPickerProps } from './types';
import UserTile from '@app/shared/ui-kit/atoms/UserTile';
import { TextInput } from '@mantine/core';
import { useEffect, useMemo, useState } from 'react';
import { IconSend } from '@tabler/icons-react';
import { palette } from '@app/shared/ui-kit/colors';
import { spacing } from '@app/shared/themes/base';
import { FONT_WEIGHT } from '@app/shared/ui-kit/fonts';
import { useTranslation } from 'react-i18next';
import { userPickerClassNames, dropdownOption } from './styles.css';

export default function UserPicker({
  availableUsers,
  allowCustomEmail,
  placeholder,
  onPickUser,
  comboboxDropdownProps,
  onOpen,
  onClose,
  ...rootProps
}: UserPickerProps) {
  const { t } = useTranslation('User');

  const [search, setSearch] = useState('');

  const combobox = useCombobox({
    onDropdownClose: () => combobox.resetSelectedOption(),
  });

  const onOptionSubmit = (value: string) => {
    onPickUser?.(value.toLowerCase());
    setSearch('');
    combobox.closeDropdown();
  };

  const isEmail = (value: string) => {
    const segments = value.split('@');
    return (
      segments.length === 2 && segments[0].length > 0 && segments[1].length > 0
    );
  };

  const filteredAvailableUsers = useMemo(
    () =>
      search
        ? availableUsers.filter((user) =>
            user.email.toLowerCase().includes(search.toLowerCase()),
          )
        : availableUsers,
    [availableUsers, search],
  );

  useEffect(() => {
    // we need to wait for options to render before we can select first one
    combobox.selectFirstOption();
  }, [search]);

  return (
    <Combobox
      classNames={userPickerClassNames}
      store={combobox}
      size="sm"
      onOptionSubmit={onOptionSubmit}
      onOpen={onOpen}
      onClose={onClose}
    >
      <Combobox.Target>
        <TextInput
          placeholder={placeholder ?? 'Add user'}
          value={search}
          onChange={(event) => {
            setSearch(event.currentTarget.value);
            combobox.openDropdown();
          }}
          onKeyDown={(event) => {
            if (event.key === ' ') {
              onOptionSubmit(search);
            }
          }}
          onClick={() => combobox.openDropdown()}
          onFocus={() => combobox.openDropdown()}
          onBlur={() => combobox.closeDropdown()}
          {...rootProps}
        />
      </Combobox.Target>

      <Combobox.Dropdown {...comboboxDropdownProps} className={dropdownOption}>
        <Combobox.Options color="blackroomGrey">
          {filteredAvailableUsers.map((user) => (
            <Combobox.Option
              value={user.email}
              key={user.id}
              color="blackroomGrey"
            >
              <UserTile user={user} avatarProps={{ size: 'sm' }} />
            </Combobox.Option>
          ))}

          {allowCustomEmail &&
          isEmail(search) &&
          !filteredAvailableUsers.some(({ email }) => email === search) ? (
            <Combobox.Option value={search}>
              <Group gap={spacing.xs} align="center">
                <Center w={26} h={26}>
                  <IconSend size={15} color={palette.blackroomGray} />
                </Center>
                <Box
                  c={palette.blackroomDarkGray5}
                  style={{ fontWeight: FONT_WEIGHT.bold }}
                >
                  {t('User:UserPicker.inviteUser', { email: search })}
                </Box>
              </Group>
            </Combobox.Option>
          ) : filteredAvailableUsers.length === 0 ? (
            <Combobox.Option value="" disabled>
              <Box>{t('User:UserPicker.noUserFound')}</Box>
            </Combobox.Option>
          ) : null}
        </Combobox.Options>
      </Combobox.Dropdown>
    </Combobox>
  );
}
